import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Checkbox,
  FormGroup,
  FormControlLabel
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/**
 * CronBuilder Component
 * User-friendly interface to build cron expressions using dropdowns
 * Converts selections to standard cron format (m h dom mon dow)
 */
export default function CronBuilder({ value, onChange, error }) {
  const [frequency, setFrequency] = useState('daily');
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [selectedDays, setSelectedDays] = useState([1]); // For weekly - Monday by default
  const [everyNHours, setEveryNHours] = useState(6); // For hourly intervals
  
  // Parse incoming cron expression to set initial values
  useEffect(() => {
    if (value) {
      const parsed = parseCronExpression(value);
      if (parsed) {
        setFrequency(parsed.frequency);
        setHour(parsed.hour);
        setMinute(parsed.minute);
        setSelectedDays(parsed.selectedDays);
        setEveryNHours(parsed.everyNHours);
      }
    }
  }, []);

  // Build cron expression whenever selections change
  useEffect(() => {
    const cronExpression = buildCronExpression();
    if (cronExpression !== value) {
      onChange(cronExpression);
    }
  }, [frequency, hour, minute, selectedDays, everyNHours]);

  const buildCronExpression = () => {
    switch (frequency) {
      case 'daily':
        // Run every day at specified time
        return `${minute} ${hour} * * *`;
      
      case 'weekly':
        // Run on specific days of the week at specified time
        const days = selectedDays.sort((a, b) => a - b).join(',');
        return `${minute} ${hour} * * ${days}`;
      
      case 'hourly':
        // Run every N hours
        return `${minute} */${everyNHours} * * *`;
      
      case 'every-hour':
        // Run every hour at specified minute
        return `${minute} * * * *`;
      
      case 'weekdays':
        // Run Monday to Friday at specified time
        return `${minute} ${hour} * * 1-5`;
      
      case 'weekends':
        // Run Saturday and Sunday at specified time
        return `${minute} ${hour} * * 0,6`;
      
      default:
        return `${minute} ${hour} * * *`;
    }
  };

  const parseCronExpression = (cron) => {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return null;

    const [min, hr, dom, mon, dow] = parts;
    const result = {
      minute: min === '*' ? 0 : parseInt(min),
      hour: 2,
      frequency: 'daily',
      selectedDays: [1],
      everyNHours: 6
    };

    // Parse hour
    if (hr.startsWith('*/')) {
      result.everyNHours = parseInt(hr.substring(2));
      result.frequency = 'hourly';
      result.hour = 0;
    } else if (hr === '*') {
      result.frequency = 'every-hour';
      result.hour = 0;
    } else {
      result.hour = parseInt(hr);
    }

    // Parse day of week
    if (dow !== '*') {
      if (dow === '1-5') {
        result.frequency = 'weekdays';
      } else if (dow === '0,6' || dow === '6,0') {
        result.frequency = 'weekends';
      } else if (dow.includes(',')) {
        result.frequency = 'weekly';
        result.selectedDays = dow.split(',').map(d => parseInt(d));
      } else {
        result.frequency = 'weekly';
        result.selectedDays = [parseInt(dow)];
      }
    }

    return result;
  };

  const getHumanReadableSchedule = () => {
    switch (frequency) {
      case 'daily':
        return `Every day at ${formatTime(hour, minute)}`;
      
      case 'weekly':
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const days = selectedDays.map(d => dayNames[d]).join(', ');
        return `Every ${days} at ${formatTime(hour, minute)}`;
      
      case 'hourly':
        return `Every ${everyNHours} hours at ${minute} minutes past the hour`;
      
      case 'every-hour':
        return `Every hour at ${minute} minutes past the hour`;
      
      case 'weekdays':
        return `Every weekday (Mon-Fri) at ${formatTime(hour, minute)}`;
      
      case 'weekends':
        return `Every weekend (Sat-Sun) at ${formatTime(hour, minute)}`;
      
      default:
        return '';
    }
  };

  const formatTime = (h, m) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const minuteStr = m.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  };

  const dayOptions = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  const handleDayToggle = (day) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        // Don't allow removing all days
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Frequency Selection */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Schedule Frequency</InputLabel>
            <Select
              value={frequency}
              label="Schedule Frequency"
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="daily">
                <Box>
                  <Typography variant="body1">Daily</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs every day at a specific time
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="weekdays">
                <Box>
                  <Typography variant="body1">Weekdays</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs Monday through Friday
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="weekly">
                <Box>
                  <Typography variant="body1">Specific Days</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose which days of the week
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="weekends">
                <Box>
                  <Typography variant="body1">Weekends</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs Saturday and Sunday
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="hourly">
                <Box>
                  <Typography variant="body1">Every N Hours</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs multiple times per day
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="every-hour">
                <Box>
                  <Typography variant="body1">Every Hour</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs once per hour
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Day Selection for Weekly */}
        {frequency === 'weekly' && (
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="600" sx={{ mb: 2 }}>
              Select Days of the Week
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {dayOptions.map(day => (
                <Chip
                  key={day.value}
                  label={day.label}
                  onClick={() => handleDayToggle(day.value)}
                  color={selectedDays.includes(day.value) ? 'primary' : 'default'}
                  variant={selectedDays.includes(day.value) ? 'filled' : 'outlined'}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Grid>
        )}

        {/* Hour Interval for Hourly */}
        {frequency === 'hourly' && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Run Every</InputLabel>
              <Select
                value={everyNHours}
                label="Run Every"
                onChange={(e) => setEveryNHours(e.target.value)}
              >
                <MenuItem value={1}>1 Hour</MenuItem>
                <MenuItem value={2}>2 Hours</MenuItem>
                <MenuItem value={3}>3 Hours</MenuItem>
                <MenuItem value={4}>4 Hours</MenuItem>
                <MenuItem value={6}>6 Hours</MenuItem>
                <MenuItem value={8}>8 Hours</MenuItem>
                <MenuItem value={12}>12 Hours</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Time Selection (for daily, weekly, weekdays, weekends) */}
        {['daily', 'weekly', 'weekdays', 'weekends'].includes(frequency) && (
          <>
            <Grid item xs={7} md={4}>
              <FormControl fullWidth>
                <InputLabel>Hour</InputLabel>
                <Select
                  value={hour}
                  label="Hour"
                  onChange={(e) => setHour(e.target.value)}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {formatTime(i, 0).split(':')[0] + ' ' + formatTime(i, 0).split(' ')[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={5} md={3}>
              <FormControl fullWidth>
                <InputLabel>Minute</InputLabel>
                <Select
                  value={minute}
                  label="Minute"
                  onChange={(e) => setMinute(e.target.value)}
                >
                  {[0, 15, 30, 45].map(m => (
                    <MenuItem key={m} value={m}>
                      :{m.toString().padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* Minute Selection (for hourly and every-hour) */}
        {['hourly', 'every-hour'].includes(frequency) && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>At Minute</InputLabel>
              <Select
                value={minute}
                label="At Minute"
                onChange={(e) => setMinute(e.target.value)}
              >
                {[0, 15, 30, 45].map(m => (
                  <MenuItem key={m} value={m}>
                    :{m.toString().padStart(2, '0')} past the hour
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Human Readable Summary */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<AccessTimeIcon />}>
            <Typography variant="body2" fontWeight="600">
              Schedule Summary:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {getHumanReadableSchedule()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Cron expression: <code>{buildCronExpression()}</code>
            </Typography>
          </Alert>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
